import { useCallback, useState } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";

import type { CompoundRecordComposerSearchCondition } from "~/features/compoundRecordComposers/types";
import { v2Route } from "~/v2/routes";
import { CompoundRecordComposersPresenter } from "./compoundRecordComposers.presenter";

const DEFAULT_PAGE_SIZE = 10;

export function CompoundRecordComposersContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.compoundRecordComposers);

  // Search conditions live in local state only (not URL), matching v1 behaviour.
  const [searchConditions, setSearchConditions] = useState<CompoundRecordComposerSearchCondition[]>([]);

  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const selectedComposer = params.selectedComposer ?? null;

  const onConditionsChange = useCallback((next: CompoundRecordComposerSearchCondition[]) => {
    setSearchConditions(next);
  }, []);

  const onPageReset = useCallback(() => {
    setParams((prev) => ({ ...prev, page: undefined }));
  }, [setParams]);

  const onPageIndexChange = useCallback(
    (next: number) => {
      setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      setParams((prev) => ({
        ...prev,
        pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedComposer: id })), [setParams]);

  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedComposer: undefined })),
    [setParams],
  );

  return (
    <CompoundRecordComposersPresenter
      searchConditions={searchConditions}
      onConditionsChange={onConditionsChange}
      onPageReset={onPageReset}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      selectedComposer={selectedComposer}
      onSelect={onSelect}
      onCloseSelected={onCloseSelected}
    />
  );
}
